<script setup>
import AppLogo from 'components/AppLogo.vue';
import TextLink from 'components/TextLink.vue';
import AppRoute from 'src/router/app-route';
import ExternalRoute from 'src/router/external-route';
import { Route } from 'src/router/routes';
import { RequestType } from 'src/services/ticket-service';
import { openComponentDialog } from 'src/utils/quasar-util';
import { getXprtJobsOrigin } from 'src/utils/xprt-jobs-route-util';
import { useAuthStore } from 'stores/auth-store';
import { defineAsyncComponent } from 'vue';

const authStore = useAuthStore();

const xprtDevLinks = [
    {
        label: 'Market',
        route: AppRoute.build(Route.MarketOverview)
    },
    {
        label: 'Methodology',
        route: AppRoute.build(Route.Methodology)
    },
    {
        label: 'About Xprt Dev',
        route: AppRoute.build(Route.AboutUs)
    },
    {
        label: 'Request a Feature',
        route: () => {
            if (!authStore.userAuthenticated) {
                return openUserLoginDialog();
            }
            return openComponentDialog(defineAsyncComponent(() => import('components/TicketCreateDialog.vue')), {
                requestType: RequestType.FEATURE_REQUEST
            });
        }
    },
    {
        label: 'Report a Bug',
        route: () => {
            if (!authStore.userAuthenticated) {
                return openUserLoginDialog();
            }
            return openComponentDialog(defineAsyncComponent(() => import('components/TicketCreateDialog.vue')), {
                requestType: RequestType.BUG_REPORT
            });
        }
    },
    {
        label: 'FAQs',
        route: AppRoute.build(Route.FAQs)
    }
];

const companyLinks = [
    {
        label: '0x234',
        route: ExternalRoute.build('https://0x234.io')
    },
    {
        label: 'Privacy Policy',
        route: AppRoute.build(Route.PrivacyPolicy)
    },
    {
        label: 'Terms of Service',
        route: AppRoute.build(Route.TermsOfService)
    }
];

const networkLinks = [
    {
        label: 'DoubleTime',
        route: ExternalRoute.build('https://doubletime.io')
    },
    {
        label: 'Xprt Jobs',
        route: ExternalRoute.build(getXprtJobsOrigin())
    }
];

const linkGroups = [
    { title: 'Xprt Dev', links: xprtDevLinks },
    { title: 'Company', links: companyLinks },
    { title: 'Network', links: networkLinks }
];

function openUserLoginDialog() {
    return openComponentDialog(defineAsyncComponent(() => import('components/UserLoginDialog.vue')), {
        headline: 'Help improve Xprt Dev&hellip;',
        message: 'Log in to report a problem or suggest an improvement to the market and account tools.'
    });
}
</script>

<template>
    <footer class="app-footer q-px-md">
        <div class="container-md q-py-xl">
            <div class="footer-grid">
                <div class="footer-brand">
                    <AppLogo height="28px"/>
                    <p class="text-body2 text-hint q-mt-md q-mb-xs">Market context for Australian technology workers.</p>
                    <p class="font-mono text-caption text-hint q-my-none">
                        by <a class="font-mono" href="https://0x234.io" rel="noopener noreferrer" target="_blank">0x234</a>
                    </p>
                </div>
                <nav v-for="group of linkGroups" :key="group.title" :aria-label="group.title" class="footer-group">
                    <p class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-sm">{{ group.title }}</p>
                    <ul class="footer-links q-pl-none q-my-none">
                        <li v-for="link of group.links" :key="link.label" class="q-mb-xs">
                            <TextLink :label="link.label" :route="link.route"/>
                        </li>
                    </ul>
                </nav>
                <div class="footer-group">
                    <p class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-sm">Social</p>
                    <div class="row q-gutter-sm">
                        <a
                            aria-label="0x234 on LinkedIn"
                            class="brand-link no-hover-decoration"
                            href="https://linkedin.com/company/0x234"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <img alt="" class="brand-icon" src="~assets/brand_linkedin-white.svg"/>
                        </a>
                        <a
                            aria-label="Xprt on X"
                            class="brand-link no-hover-decoration"
                            href="https://twitter.com/xprtdev"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <img alt="" class="brand-icon" src="~assets/brand_x-white.svg"/>
                        </a>
                    </div>
                </div>
            </div>
            <div class="footer-rule row items-center justify-between q-gutter-y-sm q-mt-xl q-pt-lg">
                <span class="font-mono text-caption text-hint">&copy; 2026 0x234 &middot; All rights reserved</span>
                <span class="font-mono text-caption text-hint">
                    Figures observed from active Xprt Jobs listings &middot; <a class="font-mono" href="/methodology">Read the notes</a>
                </span>
            </div>
        </div>
    </footer>
</template>

<style lang="scss" scoped>
.app-footer {
    border-top: 1px solid rgba($primary, 0.16);

    .footer-grid {
        display: grid;
        gap: 40px 48px;
        grid-template-columns: minmax(0, 2fr) repeat(4, minmax(0, 1fr));
    }

    .footer-brand {
        max-width: 320px;
    }

    .footer-links {
        list-style: none;
    }

    .footer-rule {
        border-top: 1px solid rgba($grey-7, 0.2);
    }

    .brand-link {
        display: inline-block;
        opacity: 0.8;
        transition: opacity 120ms ease-in-out;

        &:hover {
            opacity: 1;
        }
    }

    .brand-icon {
        display: block;
        filter: invert(1);
        height: 24px;
        width: 24px;
    }

    @media (max-width: $breakpoint-sm-max) {
        .footer-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .footer-brand {
            grid-column: 1 / -1;
        }
    }

    .body--dark & {
        .brand-icon {
            filter: none;
        }
    }
}
</style>
